import React from "react";
import { FORM_DIRTY_DATA_ATTR, hasDirtyFormInContainer } from "./unsavedChanges";

/**
 * Отслеживает `data-form-dirty` внутри контейнера формы (для unsaved-guard на уровне surface).
 */
export function useFormDirtyTracking(enabled: boolean): {
    setTrackingRoot: (element: HTMLDivElement | null) => void;
    hasUnsavedChanges: boolean;
} {
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement | null>(null);

    const setTrackingRoot = React.useCallback((element: HTMLDivElement | null) => {
        rootRef.current = element;
    }, []);

    React.useEffect(() => {
        if (!enabled) {
            setHasUnsavedChanges(false);
            return;
        }

        const contentContainer = rootRef.current;
        if (contentContainer == null) {
            return;
        }

        let rafId = 0;
        const updateUnsavedChanges = () => {
            setHasUnsavedChanges(hasDirtyFormInContainer(contentContainer));
        };
        const scheduleUpdate = () => {
            if (rafId !== 0) {
                return;
            }
            rafId = window.requestAnimationFrame(() => {
                rafId = 0;
                updateUnsavedChanges();
            });
        };

        updateUnsavedChanges();
        const observer = new MutationObserver(scheduleUpdate);
        observer.observe(contentContainer, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: [FORM_DIRTY_DATA_ATTR],
        });

        return () => {
            observer.disconnect();
            if (rafId !== 0) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, [enabled]);

    return { setTrackingRoot, hasUnsavedChanges };
}
